import { Link, useParams } from "react-router-dom";
import { useReceiptDetail, useReceiptVerify } from "@/api/hooks";

function formatTime(unixSec: number) {
  if (!unixSec) return "—";
  return new Date(unixSec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z";
}

export function ReceiptDetailPage() {
  const { id } = useParams();
  const detail = useReceiptDetail(id);
  const verify = useReceiptVerify(id);

  if (detail.isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (detail.error) {
    return (
      <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        Receipt not found: <span className="font-mono">{id}</span>
      </div>
    );
  }
  const r = detail.data!;
  const v = verify.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/receipts" className="text-sm text-blue-700 hover:underline">
          ← back to receipts
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-semibold">
          Receipt <span className="font-mono">{r.id}</span>
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Uploaded {formatTime(r.uploaded_at_sec)} from{" "}
          <span className="font-mono">{r.source_ip}</span>, {r.size_bytes} bytes,{" "}
          {r.decision_count} per-token decisions.
        </p>
      </div>

      <section className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-slate-600 dark:text-slate-400">
            Server-side verification
          </h2>
          {r.verified ? (
            <span className="rounded bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 text-xs font-bold">
              match = YES
            </span>
          ) : (
            <span className="rounded bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 py-0.5 text-xs font-bold">
              match = NO
            </span>
          )}
        </div>
        {v && (
          <table className="text-xs font-mono">
            <tbody>
              <tr><td className="pr-3 text-slate-500 dark:text-slate-400">key</td><td>{v.key_hex}</td></tr>
              <tr><td className="pr-3 text-slate-500 dark:text-slate-400">session</td><td>{v.session_hash}</td></tr>
              <tr><td className="pr-3 text-slate-500 dark:text-slate-400">silicon</td><td>{v.hmac_sig}</td></tr>
              <tr><td className="pr-3 text-slate-500 dark:text-slate-400">reference</td><td>{v.reference}</td></tr>
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded border border-blue-200 bg-blue-50 p-4 text-sm">
        <p className="mb-2">
          Reproduce this check yourself in 4 lines of Python (no server trust
          required) — the patent-load-bearing path is offline verification:
        </p>
        <pre className="bg-white dark:bg-slate-800 p-3 rounded text-xs overflow-x-auto">{`import hmac, hashlib
key = bytes.fromhex("${v?.key_hex || ""}")
session = bytes.fromhex("${r.session_hash}")
assert hmac.new(key, session, hashlib.sha256).hexdigest() == "${r.hmac_sig}"`}</pre>
        <p className="mt-2">
          Or the canonical tool:{" "}
          <code className="font-mono bg-white dark:bg-slate-800 px-1 rounded">
            python3 docs/patents/stage1/annexes/A_verify.py &lt;bundle&gt;
          </code>
        </p>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-600 dark:text-slate-400 mb-2">
          Raw bundle
        </h2>
        <pre className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded p-3 text-xs overflow-x-auto whitespace-pre">
          {r.raw_bundle}
        </pre>
      </section>
    </div>
  );
}
